import { useEffect } from 'react';
import { createClient } from '@supabase/supabase-js';

// Initialize Supabase client
const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY;
const supabase = createClient(supabaseUrl, supabaseAnonKey);

// Generate or retrieve a persistent visitor ID
const getVisitorId = (): string => {
  const VISITOR_ID_KEY = 'analytics_visitor_id';
  let visitorId = localStorage.getItem(VISITOR_ID_KEY);
  
  if (!visitorId) {
    visitorId = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
    localStorage.setItem(VISITOR_ID_KEY, visitorId);
  }
  
  return visitorId;
};

// Generate or retrieve a session ID (expires after 30 minutes of inactivity)
const getSessionId = (): string => {
  const SESSION_ID_KEY = 'analytics_session_id';
  const SESSION_TIMESTAMP_KEY = 'analytics_session_timestamp';
  const SESSION_TIMEOUT = 30 * 60 * 1000; // 30 minutes
  
  const now = Date.now();
  const lastActivity = localStorage.getItem(SESSION_TIMESTAMP_KEY);
  
  let sessionId = localStorage.getItem(SESSION_ID_KEY);
  
  // Create new session if none exists or if session expired
  if (!sessionId || !lastActivity || (now - parseInt(lastActivity)) > SESSION_TIMEOUT) {
    sessionId = 'sess_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
    localStorage.setItem(SESSION_ID_KEY, sessionId);
  }
  
  // Update last activity timestamp
  localStorage.setItem(SESSION_TIMESTAMP_KEY, now.toString());
  
  return sessionId;
};

// Extract UTM parameters from URL
const getUTMParameters = () => {
  const params = new URLSearchParams(window.location.search);
  
  return {
    utm_source: params.get('utm_source') || null,
    utm_medium: params.get('utm_medium') || null,
    utm_campaign: params.get('utm_campaign') || null,
  };
};

// Store UTM parameters in sessionStorage (persist through opt-in flow)
const storeUTMParameters = () => {
  const UTM_STORAGE_KEY = 'analytics_utm_params';
  
  // Check if UTM params already stored in this session
  const storedUTM = sessionStorage.getItem(UTM_STORAGE_KEY);
  if (storedUTM) {
    return JSON.parse(storedUTM);
  }
  
  // Get UTM from current URL
  const utmParams = getUTMParameters();
  
  // Only store if at least one UTM parameter exists
  if (utmParams.utm_source || utmParams.utm_medium || utmParams.utm_campaign) {
    sessionStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(utmParams));
    return utmParams;
  }
  
  // Return null values if no UTM
  return { utm_source: null, utm_medium: null, utm_campaign: null };
};

// Retrieve stored UTM parameters
const getStoredUTMParameters = () => {
  const UTM_STORAGE_KEY = 'analytics_utm_params';
  const storedUTM = sessionStorage.getItem(UTM_STORAGE_KEY);
  
  if (storedUTM) {
    return JSON.parse(storedUTM);
  }
  
  return { utm_source: null, utm_medium: null, utm_campaign: null };
};

// Detect device type
const getDeviceType = (): 'desktop' | 'mobile' | 'tablet' => {
  const userAgent = navigator.userAgent.toLowerCase();
  const width = window.innerWidth;
  
  // Check for tablet
  if (/(ipad|tablet|playbook|silk)|(android(?!.*mobile))/i.test(userAgent)) {
    return 'tablet';
  }
  
  // Check for mobile
  if (/mobile|iphone|ipod|android|blackberry|opera mini|iemobile/i.test(userAgent) || width < 768) {
    return 'mobile';
  }
  
  return 'desktop';
};

// Track page visit to Supabase
const trackPageVisit = async (page: string) => {
  const visitorId = getVisitorId();
  const sessionId = getSessionId();
  const utmParams = storeUTMParameters();
  const deviceType = getDeviceType();
  
  try {
    const { error } = await supabase
      .from('page_visits')
      .insert({
        visitor_id: visitorId,
        session_id: sessionId,
        page,
        utm_source: utmParams.utm_source,
        utm_medium: utmParams.utm_medium,
        utm_campaign: utmParams.utm_campaign,
        device_type: deviceType,
      });
    
    if (error) {
      console.error('Error tracking page visit:', error);
    }
  } catch (err) {
    console.error('Failed to track page visit:', err);
  }
};

// Track opt-in conversion (Step 1)
export const trackOptIn = async () => {
  const visitorId = getVisitorId();
  const sessionId = getSessionId();
  const utmParams = getStoredUTMParameters();
  
  try {
    const { error } = await supabase
      .from('opt_ins')
      .insert({
        visitor_id: visitorId,
        session_id: sessionId,
        utm_source: utmParams.utm_source,
        utm_medium: utmParams.utm_medium,
        utm_campaign: utmParams.utm_campaign,
      });
    
    if (error) {
      console.error('Error tracking opt-in:', error);
    }
  } catch (err) {
    console.error('Failed to track opt-in:', err);
  }
};

// Track affiliate click (Step 2)
export const trackAffiliateClick = async () => {
  const visitorId = getVisitorId();
  const sessionId = getSessionId();
  const utmParams = getStoredUTMParameters();
  
  try {
    const { error } = await supabase
      .from('affiliate_clicks')
      .insert({
        visitor_id: visitorId,
        session_id: sessionId,
        utm_source: utmParams.utm_source,
        utm_medium: utmParams.utm_medium,
        utm_campaign: utmParams.utm_campaign,
      });
    
    if (error) {
      console.error('Error tracking affiliate click:', error);
    }
  } catch (err) {
    console.error('Failed to track affiliate click:', err);
  }
};

// Hook to track page visits
export const usePageVisit = (pageName: string) => {
  useEffect(() => {
    trackPageVisit(pageName);
  }, [pageName]);
};

// Hook to observe form submission (ConvertKit)
export const useFormSubmissionTracking = () => {
  useEffect(() => {
    let hasTracked = false;
    
    const checkForSuccess = () => {
      if (hasTracked) return;
      
      // Check for ConvertKit success message
      const successMessage = document.querySelector('.formkit-alert-success');
      
      if (successMessage) {
        hasTracked = true;
        trackOptIn();
      }
    };
    
    // Create MutationObserver to watch for form submission success
    const observer = new MutationObserver(() => {
      checkForSuccess();
    });
    
    // Observe the entire document for changes
    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
    
    // Also check periodically (fallback)
    const interval = setInterval(checkForSuccess, 1000);
    
    return () => {
      observer.disconnect();
      clearInterval(interval);
    };
  }, []);
};

export default usePageVisit;